import {useIntl} from "react-intl";
import React, {useMemo, useState} from "react";
import {Accordion, usePopup} from "../../../UIElements";
import {useSupportBotContext} from "../SupportBotData";
import {filterTopicsFromCategories, getNewCategoryKey, getNewTopicKey} from "../../../DataFunctions";
import {PostCodes} from "../../../api/APISurvey";
import {useSnackbar} from "../../../Content";
import {getUniqueKey} from "../../../Formats";

const SupportTopicCreator = ({botData, updateBotData, updateNestedData}) => {
    const intl = useIntl()
    const { showPopup } = usePopup()
    const { data: { supportTopicCodes, supportBots }, refreshData } = useSupportBotContext()

    const topicData = filterTopicsFromCategories(supportTopicCodes)

    return <button
        className={"new-topic"}
        onClick={()=>{showPopup(
            <SupportTopicSelector
                botData={botData}
                updateBotData={updateBotData}
                updateNestedData={updateNestedData}
                supportTopicCodes={topicData}
                supportBots={supportBots}
                refreshData={refreshData}
            />, 'full-width')}}>
        + {intl.formatMessage({
        id: 'bot.instructions.addTopic',
        defaultMessage: 'Add Topic',
    })}
    </button>
}

const SupportTopicSelector = ({botData, updateBotData, updateNestedData, supportTopicCodes, supportBots, refreshData}) => {
    const intl = useIntl()
    const [searchText, setSearchText] = useState("")
    const { hidePopup } = usePopup()
    const {setSnackbarData} = useSnackbar()

    const usedCategories = botData?.supportTopics || []
    const topicData = botData?.topicData || {}

    // Helper function to check if a topic is used in multiple bot modules
    const isTopicSharedAcrossModules = (topicId) => {
        if (!supportBots || !Array.isArray(supportBots)) return false;

        const currentVersionGroup = botData.versionGroup;

        // Check if any OTHER version group (not current) uses this topic
        return supportBots.some(bot => {
            // Skip the current version group
            if (bot?.config?.versionGroup === currentVersionGroup) {
                return false;
            }

            return bot?.config?.supportTopics?.includes(topicId);
        });
    };

    const themes = useMemo(() => {
        return Object.entries(supportTopicCodes).reduce((acc, [key, item]) => {
            const { theme } = item
            if (!acc[theme]) {
                acc[theme] = []
            }
            acc[theme].push({
                key,
                ...item,
                isShared: isTopicSharedAcrossModules(key)
            })
            return acc
        }, {})
    }, [supportTopicCodes, supportBots, botData.versionGroup])

    const filteredThemes = useMemo(() => {
        if (!searchText) return themes
        const lowerSearchText = searchText.toLowerCase()
        return Object.entries(themes).reduce((acc, [theme, items]) => {
            const filteredItems = items.filter(item =>
                item.title.toLowerCase().includes(lowerSearchText) ||
                intl.formatMessage({id:'theme.'+theme, defaultMessage:'theme'}).toLowerCase().includes(lowerSearchText)
            )
            if (filteredItems.length > 0) {
                acc[theme] = filteredItems
            }
            return acc
        }, {})
    }, [searchText, themes])

    const isMatch = useMemo(() => {
        if (!searchText) return true
        const lowerSearchText = searchText.toLowerCase()
        return Object.values(supportTopicCodes).reduce((acc, item) => {
            const { theme, title } = item
            if (title.toLowerCase().includes(lowerSearchText)) {
                acc[theme] = true
            }
            return acc
        }, {})
    }, [searchText, supportTopicCodes])

    const onItemClick = (topic, newTopicData) => {
        if (usedCategories.includes(topic)) {
            setSnackbarData({
                show: true,
                text: intl.formatMessage({
                    id: 'topic.alreadyInUse',
                    defaultMessage: 'This topic is already selected',
                }),
                state: "info",
                autoHide: true,
            })
            // Exit early without updating state
            return
        }
        // Add topic to supportTopics array
        updateBotData("supportTopics", [
            ...usedCategories,
            topic
        ]);
        // Add topic data to topicData object
        if (newTopicData) {
            updateBotData("topicData", {
                ...topicData,
                [topic]: newTopicData
            })
        } else {
            updateBotData("topicData", {
                ...topicData,
                [topic]: supportTopicCodes[topic]
            })
        }

        hidePopup()
    }

    return <div className={"category-selection"}>
        <h1>
            {intl.formatMessage({
                id: 'bot.instructions.addTopic.header',
                defaultMessage: 'Add New Support Topic for Bot',
            })}
        </h1>
        <input type={"text"} className={"search"} value={searchText}
               autoFocus
               onChange={e => {
                   setSearchText(e.target.value)
               }}
               placeholder={intl.formatMessage(
                   {
                       id: 'codes.search.placeholder',
                       defaultMessage: 'Search Support Topics',
                   })}/>
        <div className={"accordion-container"}>
            {Object.entries(filteredThemes).map(([theme, items]) => (
                <Accordion key={theme}
                           themeKey={theme}
                           theme={theme !== "undefined" ? intl.formatMessage({id:'theme.'+theme, defaultMessage:theme}) : intl.formatMessage({id:'missingTheme', defaultMessage:"Missing theme"})}
                           items={items}
                           onItemClick={onItemClick}
                           isOpen={isMatch[theme]}
                           usedCategories={usedCategories}
                           customAddNewTopic={<AddNewTopicComponent
                               theme={theme}
                               onItemClick={onItemClick}
                               supportTopicCodes={supportTopicCodes}
                               refreshData={refreshData}
                           />}
                />
            ))}
        </div>
    </div>
}

const AddNewTopicComponent = ({theme, onItemClick, refreshData}) => {
    const intl = useIntl()
    const [showInput, setShowInput] = useState(false)
    const [categoryTitle, setCategoryTitle] = useState("")
    const postCode = PostCodes()
    const {setSnackbarData} = useSnackbar()

    const handleSubmit = (e) => {
        e.preventDefault()
        if (categoryTitle?.length < 1) return

        const newKey = `TOPIC_${getUniqueKey()}`
        const newCodeValue = {
            "theme": theme,
            "title": categoryTitle.trim(),
            "type": "SUPPORTBOT",
            "requesttype": "requestWithConsent",
            "instructions": ""
        }
        const codeToPost = [{
            codeset: 'category',
            code: newKey,
            value: newCodeValue,
        }]

        postCode.mutate(codeToPost, {
            onError: error => {
                console.log(error)
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage(
                        {
                            id: 'addNewTopic.error',
                            defaultMessage: 'Adding a new topic failed. Please try again.',
                        }),
                    state: "error",
                    autoHide: true,
                })
            },
            onSuccess: () => {
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage(
                        {
                            id: 'addNewTopic.success',
                            defaultMessage: 'New topic added',
                        }),
                    state: "success",
                    autoHide: true,
                })

                onItemClick(newKey, newCodeValue)

                if (refreshData) {
                    refreshData()
                }
            }
        })
    }

    if (showInput) {
        return <form className={"category-input"} onSubmit={handleSubmit}>
            <input type={"text"} autoFocus value={categoryTitle} onChange={e => setCategoryTitle(e.target.value)} />
            <input type={"submit"} value={intl.formatMessage({
                id: 'addTopic',
                defaultMessage: '+ Add Topic',
            })} />
        </form>
    }

    return <div className={"item add-new"} onClick={() => setShowInput(true)}>
        {intl.formatMessage({
            id: 'addTopic',
            defaultMessage: '+ Add Topic',
        })}
    </div>
}

export default SupportTopicCreator